import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { USER } from "../../config/constants";
import socket, { APP_UPDATE_AVAILABLE } from "../../socket";
import { getFieldErrors, IFieldError } from "../helpers";
import { IEnumCustomer } from "./customersReducer";
import { tryConfirmLogin } from "./authReducer";

export interface ICompany {
    _id: string;
    name: string;

    //location
    country: string;
    state: string;
    mainOfficeAddress?: string;

    //contacts
    phoneNumber: string;
    faxNumber?: string;
    email: string;
    website?: string;

    workers: IEnumCustomer,
    sitesCount?: number
}

export interface IEnumCompany {
    _id: string;
    name: string;
    email: string;
    phoneNumber: string;
    workersCount: number,
    sitesCount: number
}

class RegCompanyErrs {
    form: string | null = null;
    name: string | null = null;
    country: string | null = null;
    state: string | null = null;
    mainOfficeAddress: string | null = null;
    email: string | null = null;
    website: string | null = null;
    phoneNumber: string | null = null;
    faxNumber: string | null = null;

    compName: string | null = null;
    compCountry: string | null = null;
    compState: string | null = null;
    compMainOfficeAddress: string | null = null;
    compEmail: string | null = null;
    compWebsite: string | null = null;
    compPhoneNumber: string | null = null;
    compFaxNumber: string | null = null;
}

export interface ICompaniesState {
    registeringCompany: boolean;
    registeredCompany: boolean;
    regCompanyErrs: RegCompanyErrs;
    fetchingCompanies: boolean;
    companies: IEnumCompany[];
    fetchCompaniesErr: string | null;
    fetchingMainCompany: boolean;
    mainCompany: ICompany | null;
    fetchMainCompanyErr: string | null;
    updatingCompany: boolean;
    updatedCompany: boolean;
    updateCompanyErrs: RegCompanyErrs;
}

const initialState = {
    registeringCompany: false,
    registeredCompany: false,
    regCompanyErrs: new RegCompanyErrs(),
    fetchingCompanies: false,
    companies: [],
    fetchCompaniesErr: null,
    fetchingMainCompany: false,
    mainCompany: null,
    fetchMainCompanyErr: null,
    updatingCompany: false,
    updatedCompany: false,
    updateCompanyErrs: new RegCompanyErrs(),
} as ICompaniesState;

const companiesSlice = createSlice({
    name: 'companies',
    initialState,
    reducers: {
        clearRegCompanyFieldErr: (state: ICompaniesState, action: PayloadAction<string>) => {
            state.regCompanyErrs[action.payload as keyof RegCompanyErrs] = null;
        },
        clearRegCompanyErrs: (state: ICompaniesState) => {
            state.regCompanyErrs = new RegCompanyErrs();
        },
        tryResetRegCompanyFormErr: (state: ICompaniesState) => {
            let shouldReset = true;

            for (let key in state.regCompanyErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.regCompanyErrs[key as keyof RegCompanyErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.regCompanyErrs.form = null;
            }
        },
        resetRegisteredCompany: (state: ICompaniesState) => {
            state.registeredCompany = false;
        },
        regCompanyStart: (state: ICompaniesState) => {
            state.regCompanyErrs = new RegCompanyErrs();
            state.registeredCompany = false;
            state.registeringCompany = true;
        },
        regCompanyPass: (state: ICompaniesState, action: PayloadAction<ICompany>) => {
            state.registeredCompany = true;
            // state.mainCompany = action.payload;
            state.registeringCompany = false;
        },
        regCompanyFail: (state: ICompaniesState, action: PayloadAction<IFieldError[]>) => {
            state.registeringCompany = false;


            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.regCompanyErrs) {
                    if (err.field === key) {
                        state.regCompanyErrs[key as keyof RegCompanyErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.regCompanyErrs.form) {
                state.regCompanyErrs.form = 'Form incorrectly filled.';
            }
        },
        clearFetchCompaniesErr: (state: ICompaniesState) => {
            state.fetchCompaniesErr = null;
        },
        fetchCompaniesStart: (state: ICompaniesState) => {
            state.fetchCompaniesErr = null;
            state.fetchingCompanies = true;
        },
        fetchCompaniesPass: (state: ICompaniesState, action: PayloadAction<IEnumCompany[]>) => {
            state.fetchingCompanies = false;

            state.companies = action.payload;
        },
        fetchCompaniesFail: (state: ICompaniesState, action: PayloadAction<string>) => {
            state.fetchingCompanies = false;
            state.fetchCompaniesErr = action.payload;
        },
        clearFetchMainCompanyErr: (state: ICompaniesState) => {
            state.fetchMainCompanyErr = null;
        },
        clearMainCompany: (state: ICompaniesState) => {
            state.mainCompany = null;
        },
        fetchMainCompanyStart: (state: ICompaniesState) => {
            state.fetchMainCompanyErr = null;
            state.fetchingMainCompany = true;
        },
        fetchMainCompanyPass: (state: ICompaniesState, action: PayloadAction<ICompany>) => {
            state.fetchingMainCompany = false;

            state.mainCompany = action.payload;
        },
        fetchMainCompanyFail: (state: ICompaniesState, action: PayloadAction<string>) => {
            state.fetchingMainCompany = false;
            state.fetchMainCompanyErr = action.payload;
        },
        clearupdateCompanyFieldErr: (state: ICompaniesState, action: PayloadAction<string>) => {
            state.updateCompanyErrs[action.payload as keyof RegCompanyErrs] = null;
        },
        clearUpdateCompanyErrs: (state: ICompaniesState) => {
            state.updateCompanyErrs = new RegCompanyErrs();
        },
        tryResetUpdateCompanyFormErr: (state: ICompaniesState) => {
            let shouldReset = true;

            for (let key in state.updateCompanyErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.updateCompanyErrs[key as keyof RegCompanyErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.updateCompanyErrs.form = null;
            }
        },
        resetUpdatedCompany: (state: ICompaniesState) => {
            state.updatedCompany = false;
        },
        updateCompanyStart: (state: ICompaniesState) => {
            state.updatedCompany = false;
            state.updateCompanyErrs = new RegCompanyErrs();
            state.updatingCompany = true;
        },
        updateCompanyPass: (state: ICompaniesState, action: PayloadAction<ICompany>) => {
            state.updatingCompany = false;
            state.updatedCompany = true;
        },
        updateCompanyFail: (state: ICompaniesState, action: PayloadAction<IFieldError[]>) => {
            state.updatingCompany = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.updateCompanyErrs) {
                    if (err.field === key) {
                        state.updateCompanyErrs[key as keyof RegCompanyErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.updateCompanyErrs.form) {
                state.updateCompanyErrs.form = 'Form incorrectly filled.';
            }
        }
    }
});

export const {
    clearRegCompanyErrs,
    clearRegCompanyFieldErr,
    tryResetRegCompanyFormErr,
    resetRegisteredCompany,
    regCompanyStart,
    regCompanyPass,
    regCompanyFail,
    clearFetchCompaniesErr,
    fetchCompaniesStart,
    fetchCompaniesPass,
    fetchCompaniesFail,
    clearFetchMainCompanyErr,
    clearMainCompany,
    fetchMainCompanyStart,
    fetchMainCompanyPass,
    fetchMainCompanyFail,
    clearUpdateCompanyErrs,
    clearupdateCompanyFieldErr,
    tryResetUpdateCompanyFormErr,
    resetUpdatedCompany,
    updateCompanyStart,
    updateCompanyPass,
    updateCompanyFail,
} = companiesSlice.actions;

interface ITryRegCompanyArg {
    data: {
        compName: string;
        compCountry: string;
        compState: string;
        compMainOfficeAddress: string;
        compPhoneNumber: string;
        compFaxNumber: string;
        compEmail: string;
        compWebsite: string;
    };
    authToken: string;
}


export const tryRegCompany = createAsyncThunk('companies/tryRegCompanyArg', (arg: ITryRegCompanyArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/company`;

    const { data, authToken } = arg;

    dispatch(regCompanyStart());


    axios.post(endpoint, data, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(regCompanyPass(res.data));
    }).catch(err => {

        const data = err?.response?.data;


        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(regCompanyFail(fieldErrors));
    })
});


interface ITryFetchCompaniesArg {
    authToken: string;
}

export const tryFetchCompanies = createAsyncThunk('companies/tryFetchCompanies', (arg: ITryFetchCompaniesArg, { dispatch }) => {
    const { authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/companies`;

    dispatch(fetchCompaniesStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(fetchCompaniesPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({authToken}));
            return;
        }
        let error = '';

        if (data?.err) {
            error = data.err;
        } else if (data?.errors) {
            error = getFieldErrors(data.errors)[0].msg;
        } else {
            error = 'Check your network connection.';
        }

        dispatch(fetchCompaniesFail(error));
    });
});

interface ITryFetchMainCompanyArg {
    authToken: string;
    companyId: string;
}

export const tryFetchMainCompany = createAsyncThunk('companies/tryFetchMainCompany', (arg: ITryFetchMainCompanyArg, { dispatch }) => {
    const { authToken, companyId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/companies/${companyId}`;

    dispatch(fetchMainCompanyStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(fetchMainCompanyPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({authToken}));
            return;
        }
        let error = '';

        if (data?.err) {
            error = data.err;
        } else if (data?.errors) {
            error = getFieldErrors(data.errors)[0].msg;
        } else {
            error = 'Check your network connection.';
        }

        dispatch(fetchMainCompanyFail(error));
    });
});

interface ITryUpdateCompanyArg {
    companyId: string;
    data: {
        name?: string;
        country?: string;
        state?: string;
        mainOfficeAddress?: string;
        phoneNumber?: string;
        faxNumber?: string;
        email?: string;
        website?: string;
    };
    authToken: string;
}

export const tryUpdateCompany = createAsyncThunk('companies/tryUpdateCompany', (arg: ITryUpdateCompanyArg, { dispatch }) => {

    const { companyId, data, authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/companies/${companyId}`;

    dispatch(updateCompanyStart());

    axios.put(endpoint, data, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(updateCompanyPass(res.data));
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(updateCompanyFail(fieldErrors));
    });
});


export default companiesSlice.reducer;